/**
 * @fileoverview Shared report-document preset factories and gallery metadata.
 * @module core-domain/reporting/documentTemplatePresets
 *
 * Responsibilities: Provide deterministic builder configurations for tenant and
 * system report-template editors, including the Complex Inspection Report.
 * Dependencies: None; returned shapes are consumed by shared ui-admin/local-db contracts.
 * Architecture: Pure factories; no persistence, network, or host-specific wiring.
 * Notes: Complex visit scope is represented by section-driven configuration.
 */
// ============================================================
// Document Template Presets — Shared factory functions
// Each factory returns a documentConfig object matching the
// DocumentConfig interface used by both tenant-web and system-web.
// Used by: (1) system-web seeder, (2) future preset gallery UI
// ============================================================

// ---------------------------------------------------------------------------
// DSE Checklist Preset — Notice of Defensible Space Inspection
// Matches the NLTFPD / IWUIC standard form layout
// ---------------------------------------------------------------------------

/**
 * @process makeDSEPreset
 * Steps: Construct the shared defensible-space builder configuration.
 * Inputs: None.
 * Outputs: Deterministic inspection-report builder config.
 * Failure Handling: None; pure factory.
 * Idempotency: REQUIRED.
 */
export function makeDSEPreset() {
  return {
    documentType: 'inspectionReport' as const,
    layoutMode: 'form' as const,
    pageSize: 'letter' as const,
    orientation: 'portrait' as const,
    approvalMode: 'autoSend' as const,
    sections: [
      { id: 'cover', label: 'Cover Page', description: 'Optional cover with logo and summary', enabled: false },
      { id: 'header', label: 'Header', description: 'Department branding, logos, and report title', enabled: true },
      { id: 'recipientBlock', label: 'Recipient / Property', description: 'Owner/tenant name, address, APN, contacts', enabled: true },
      { id: 'subjectLine', label: 'Subject Line', description: 'RE: line with inspection type reference', enabled: false },
      { id: 'salutation', label: 'Salutation', description: 'Dear Property Owner greeting', enabled: false },
      { id: 'bodyText', label: 'Body Text', description: 'Narrative paragraphs with merge fields', enabled: false },
      { id: 'metadata', label: 'Inspection Metadata', description: 'Compliance status, type, slope, inspector', enabled: true },
      { id: 'violations', label: 'Violations', description: 'Checklist, narrative, or table of violations', enabled: true },
      { id: 'photos', label: 'Photos', description: 'Inspection photos', enabled: false },
      { id: 'comments', label: 'Comments', description: 'Free-text comments and custom fields', enabled: true },
      { id: 'contactBlock', label: 'Contact Info', description: 'For questions, contact the department', enabled: false },
      { id: 'signatures', label: 'Signatures', description: 'Inspector and responsible party signatures', enabled: true },
      { id: 'legal', label: 'Legal Text', description: 'Disclaimers and legal language', enabled: true },
      { id: 'footer', label: 'Footer', description: 'Page footer with dept info and page numbers', enabled: true },
      { id: 'staticContent', label: 'Static Attachment', description: 'Attached static pages (educational guides, etc.)', enabled: true },
    ],
    header: {
      showLogo: true,
      logoPosition: 'left' as const,
      showSecondLogo: false,
      secondLogoPosition: 'right' as const,
      showDepartmentName: true,
      showDepartmentAddress: true,
      showDepartmentPhone: true,
      showDepartmentFax: false,
      showDepartmentWebsite: false,
      customTitle: 'NOTICE OF DEFENSIBLE SPACE INSPECTION',
      customSubtitle: 'A fire district representative has inspected your property for fire hazards. You are hereby notified to correct the violation(s) indicated below.',
    },
    recipientBlock: {
      style: 'grid' as const,
      showOwnerName: true,
      showContactNumber: true,
      showApn: true,
      showInspectionAddress: true,
      showEmailAddress: true,
      showMailingAddress: true,
      showInspectionNumber: true,
    },
    metadata: {
      showComplianceStatus: true,
      showInspectionType: true,
      showSlope: true,
      showInspector: true,
      showAcreageEligible: true,
      showPhotosFlag: true,
      showCurbsideVacant: true,
      showInsuranceSolicited: true,
      showComplaintConstruction: true,
    },
    violationConfig: {
      displayFormat: 'checklist' as const,
      groupBy: 'zone' as const,
      showCodeReferences: true,
      showLocationDescriptions: false,
      conditionalDisplay: 'always' as const,
    },
    comments: {
      showCommentsBox: true,
      additionalFields: [
        { id: 'treeRemoval', label: 'TREE REMOVAL', type: 'text' as const },
        { id: 'permitNumber', label: 'PERMIT #', type: 'text' as const },
      ],
    },
    signatures: {
      style: 'initials' as const,
      inspectorSignature: true,
      responsiblePartySignature: false,
      witnessSignature: false,
      dateFields: true,
      printedNameFields: false,
      showTitle: false,
    },
    legalText: {
      enabled: true,
      content: 'All violations marked must be addressed by the owner/tenant. A re-inspection of the property will occur on or after the date indicated.\n\nThe International Wildland Urban Interface Code of 2018, as adopted by Nevada Administrative Code 477.281 and NLTFPD Resolutions 18.1 and 18.2 (IWUIC) establishes minimum regulations to mitigate the risk to life and structures from wildland fire and to mitigate structures fires from spreading to wildland fuels. Pursuant to Nevada Revised Statutes 18.118(21), Eliminate fire hazards existing within the district in the manner prescribed in NRS 474.500; owner of lands within the district shall eliminate and remove a fire hazard on the owner\'s property when directed to do so by the Board. The inspector shall have the power to render interpretations of this code as needed to conform with the intent and purpose of this code (IWUIC 104.2).',
      position: 'footer' as const,
    },
    footerConfig: {
      showDepartmentInfo: true,
      showPageNumbers: true,
      customText: 'Tree Removal Permit or Chipping Services visit: nltfpd.net/fuels-management/defensible-space/',
    },
    staticContent: {
      type: 'uploadedFile' as const,
      label: 'Defensible Space Zone Guide',
    },
  };
}

// ---------------------------------------------------------------------------
// Inspection Report Preset — Narrative report with violations & photos
// Matches the standard inspection report with dual logos, letter layout
// ---------------------------------------------------------------------------

/**
 * @process makeInspectionReportPreset
 * Steps: Construct the shared narrative inspection-report builder configuration.
 * Inputs: None.
 * Outputs: Deterministic inspection-report builder config.
 * Failure Handling: None; pure factory.
 * Idempotency: REQUIRED.
 */
export function makeInspectionReportPreset() {
  return {
    documentType: 'inspectionReport' as const,
    layoutMode: 'letter' as const,
    pageSize: 'letter' as const,
    orientation: 'portrait' as const,
    approvalMode: 'requireApproval' as const,
    sections: [
      { id: 'cover', label: 'Cover Page', description: 'Optional cover with logo and summary', enabled: false },
      { id: 'header', label: 'Header', description: 'Department branding, logos, and report title', enabled: true },
      { id: 'recipientBlock', label: 'Recipient / Property', description: 'Owner/tenant name, address, APN, contacts', enabled: true },
      { id: 'subjectLine', label: 'Subject Line', description: 'RE: line with inspection type reference', enabled: true },
      { id: 'salutation', label: 'Salutation', description: 'Dear Property Owner greeting', enabled: true },
      { id: 'bodyText', label: 'Body Text', description: 'Narrative paragraphs with merge fields', enabled: true },
      { id: 'metadata', label: 'Inspection Metadata', description: 'Compliance status, type, slope, inspector', enabled: false },
      { id: 'violations', label: 'Violations', description: 'Checklist, narrative, or table of violations', enabled: true },
      { id: 'photos', label: 'Photos', description: 'Inspection photos', enabled: true },
      { id: 'comments', label: 'Comments', description: 'Free-text comments and custom fields', enabled: false },
      { id: 'contactBlock', label: 'Contact Info', description: 'For questions, contact the department', enabled: true },
      { id: 'signatures', label: 'Signatures', description: 'Inspector and responsible party signatures', enabled: true },
      { id: 'legal', label: 'Legal Text', description: 'Disclaimers and legal language', enabled: false },
      { id: 'footer', label: 'Footer', description: 'Page footer with dept info and page numbers', enabled: true },
      { id: 'staticContent', label: 'Static Attachment', description: 'Attached static pages (educational guides, etc.)', enabled: false },
    ],
    header: {
      showLogo: true,
      logoPosition: 'left' as const,
      showSecondLogo: true,
      secondLogoPosition: 'right' as const,
      showDepartmentName: true,
      showDepartmentAddress: true,
      showDepartmentPhone: true,
      showDepartmentFax: true,
      showDepartmentWebsite: false,
      customTitle: 'INSPECTION REPORT',
    },
    recipientBlock: {
      style: 'letter-address' as const,
      showOwnerName: true,
      showContactNumber: false,
      showApn: false,
      showInspectionAddress: true,
      showEmailAddress: false,
      showMailingAddress: true,
      showInspectionNumber: false,
    },
    subjectLine: {
      prefix: 'RE:',
      template: '{{inspectionType}} Inspection — {{inspectionAddress}}',
    },
    salutation: {
      text: 'Dear Property Owner/Responsible Party,',
    },
    bodyText: {
      paragraphs: [
        {
          id: 'intro-violations',
          content: 'On {{inspectionDate}}, an inspection of the above-referenced property was conducted. The following violations of the applicable fire and life safety codes were observed and must be corrected.',
          condition: 'hasViolations' as const,
        },
        {
          id: 'intro-compliant',
          content: 'On {{inspectionDate}}, an inspection of the above-referenced property was conducted. The property was found to be in compliance with applicable fire and life safety codes.',
          condition: 'noViolations' as const,
        },
      ],
    },
    violationConfig: {
      displayFormat: 'narrative' as const,
      groupBy: 'zone' as const,
      showCodeReferences: true,
      showLocationDescriptions: true,
      conditionalDisplay: 'whenPresent' as const,
    },
    photoConfig: {
      layout: 'inline' as const,
      maxPhotos: 10,
      showCaptions: true,
      thumbnailSize: 'medium' as const,
    },
    contactBlock: {
      introText: 'If you have any questions regarding this inspection, please contact the {{departmentName}} at:',
      showPhone: true,
      showEmail: true,
      showFax: true,
    },
    signatures: {
      style: 'full' as const,
      inspectorSignature: true,
      responsiblePartySignature: false,
      witnessSignature: false,
      dateFields: true,
      printedNameFields: true,
      showTitle: true,
    },
    footerConfig: {
      showDepartmentInfo: true,
      showPageNumbers: true,
    },
  };
}

// ---------------------------------------------------------------------------
// Compliance Letter Preset — Letter confirming property compliance
// ---------------------------------------------------------------------------

/**
 * @process makeComplianceLetterPreset
 * Steps: Construct the shared compliance-letter builder configuration.
 * Inputs: None.
 * Outputs: Deterministic correspondence builder config.
 * Failure Handling: None; pure factory.
 * Idempotency: REQUIRED.
 */
export function makeComplianceLetterPreset() {
  return {
    documentType: 'certificateOfCompliance' as const,
    layoutMode: 'letter' as const,
    pageSize: 'letter' as const,
    orientation: 'portrait' as const,
    approvalMode: 'requireApproval' as const,
    sections: [
      { id: 'cover', label: 'Cover Page', description: 'Optional cover with logo and summary', enabled: false },
      { id: 'header', label: 'Header', description: 'Department branding, logos, and report title', enabled: true },
      { id: 'recipientBlock', label: 'Recipient / Property', description: 'Owner/tenant name, address, APN, contacts', enabled: true },
      { id: 'subjectLine', label: 'Subject Line', description: 'RE: line with inspection type reference', enabled: true },
      { id: 'salutation', label: 'Salutation', description: 'Dear Property Owner greeting', enabled: true },
      { id: 'bodyText', label: 'Body Text', description: 'Narrative paragraphs with merge fields', enabled: true },
      { id: 'metadata', label: 'Inspection Metadata', description: 'Compliance status, type, slope, inspector', enabled: false },
      { id: 'violations', label: 'Violations', description: 'Checklist, narrative, or table of violations', enabled: false },
      { id: 'photos', label: 'Photos', description: 'Inspection photos', enabled: false },
      { id: 'comments', label: 'Comments', description: 'Free-text comments and custom fields', enabled: false },
      { id: 'contactBlock', label: 'Contact Info', description: 'For questions, contact the department', enabled: true },
      { id: 'signatures', label: 'Signatures', description: 'Inspector and responsible party signatures', enabled: true },
      { id: 'legal', label: 'Legal Text', description: 'Disclaimers and legal language', enabled: false },
      { id: 'footer', label: 'Footer', description: 'Page footer with dept info and page numbers', enabled: true },
      { id: 'staticContent', label: 'Static Attachment', description: 'Attached static pages (educational guides, etc.)', enabled: false },
    ],
    header: {
      showLogo: true,
      logoPosition: 'left' as const,
      showSecondLogo: true,
      secondLogoPosition: 'right' as const,
      showDepartmentName: true,
      showDepartmentAddress: true,
      showDepartmentPhone: true,
      showDepartmentFax: true,
      showDepartmentWebsite: false,
      customTitle: 'COMPLIANCE LETTER',
    },
    recipientBlock: {
      style: 'letter-address' as const,
      showOwnerName: true,
      showContactNumber: false,
      showApn: false,
      showInspectionAddress: true,
      showEmailAddress: false,
      showMailingAddress: true,
      showInspectionNumber: false,
    },
    subjectLine: {
      prefix: 'RE:',
      template: 'Compliance Confirmation — {{inspectionAddress}}',
    },
    salutation: {
      text: 'Dear Property Owner/Responsible Party,',
    },
    bodyText: {
      paragraphs: [
        {
          id: 'compliance-statement',
          content: 'On {{inspectionDate}}, a re-inspection of the above-referenced property was conducted. We are pleased to inform you that the property is now in compliance with applicable fire and life safety codes. Thank you for your prompt attention to this matter.',
          condition: 'always' as const,
        },
      ],
    },
    contactBlock: {
      introText: 'If you have any questions, please contact the {{departmentName}} at:',
      showPhone: true,
      showEmail: true,
      showFax: true,
    },
    signatures: {
      style: 'full' as const,
      inspectorSignature: true,
      responsiblePartySignature: false,
      witnessSignature: false,
      dateFields: true,
      printedNameFields: true,
      showTitle: true,
    },
    footerConfig: {
      showDepartmentInfo: true,
      showPageNumbers: true,
    },
  };
}

/**
 * @process makeComplexInspectionReportPreset
 * Steps: Construct the canonical complex visit builder configuration with
 *   summary and participant sections in their required order.
 * Inputs: None.
 * Outputs: Deterministic inspectionReport DocumentConfig with complexVisit scope.
 * Failure Handling: None; pure factory.
 * Idempotency: REQUIRED.
 */
export function makeComplexInspectionReportPreset() {
  return {
    documentType: 'inspectionReport' as const,
    reportDataScope: 'complexVisit' as const,
    layoutMode: 'letter' as const,
    pageSize: 'letter' as const,
    orientation: 'portrait' as const,
    approvalMode: 'autoSend' as const,
    sections: [
      { id: 'header', label: 'Header', description: 'Department branding and report title', enabled: true },
      { id: 'complexVisitSummary', label: 'Complex Visit Summary', description: 'Complex identity and visit counts', enabled: true },
      { id: 'complexParticipants', label: 'Complex Participants', description: 'One report section per governed participant', enabled: true },
      { id: 'contactBlock', label: 'Contact Info', description: 'Department contact information', enabled: true },
      { id: 'footer', label: 'Footer', description: 'Page footer with department info and page numbers', enabled: true },
    ],
    complexVisitSummary: {
      showComplexName: true,
      showAnchor: true,
      showVisitDate: true,
      showParticipantCounts: true,
      showStandaloneSummary: true,
    },
    complexParticipants: {
      includeRootEntity: true,
      includeGovernedMembers: true,
      includeStandaloneSummary: true,
      startEachEntityOnNewPage: true,
      showEntityHeader: true,
      showEntityAddress: true,
      showEntityStatus: true,
      showQuestionsResponses: true,
      showViolations: true,
      showCorrections: true,
      showPermits: true,
      showAssets: true,
      showPhotos: true,
      showSignatures: true,
      entitySort: 'visitOrder' as const,
    },
    header: {
      showLogo: true,
      logoPosition: 'left' as const,
      showSecondLogo: true,
      secondLogoPosition: 'right' as const,
      showDepartmentName: true,
      showDepartmentAddress: true,
      showDepartmentPhone: true,
      showDepartmentFax: false,
      showDepartmentWebsite: false,
      customTitle: 'COMPLEX INSPECTION REPORT',
    },
    contactBlock: {
      introText: 'For questions about this complex inspection, contact the department at:',
      showPhone: true,
      showEmail: true,
      showFax: false,
    },
    footerConfig: { showDepartmentInfo: true, showPageNumbers: true },
  };
}

// ---------------------------------------------------------------------------
// All presets — used by future preset gallery UI
// ---------------------------------------------------------------------------

/**
 * @contract DOCUMENT_TEMPLATE_PRESETS
 * Purpose: Gallery metadata mapping stable preset IDs to pure factories.
 * Fields: id, name, description, category, and factory.
 * Rules: Factories return configs without persistence or host dependencies.
 * Sync: Consumed by tenant/system builders and gallery surfaces.
 */
export const DOCUMENT_TEMPLATE_PRESETS = [
  {
    id: 'dse-checklist',
    name: 'Notice of Defensible Space Inspection',
    description: 'Standard DSE checklist form with violation checkboxes, compliance status, and educational attachment',
    category: 'inspection',
    factory: makeDSEPreset,
  },
  {
    id: 'complex-inspection-report',
    name: 'Complex Inspection Report',
    description: 'Complex visit summary and per-participant inspection report',
    category: 'inspection',
    factory: makeComplexInspectionReportPreset,
  },
  {
    id: 'inspection-report',
    name: 'Inspection Report',
    description: 'Narrative inspection report with violations, photos, and contact block',
    category: 'inspection',
    factory: makeInspectionReportPreset,
  },
  {
    id: 'compliance-letter',
    name: 'Compliance Letter',
    description: 'Letter confirming property compliance after re-inspection',
    category: 'correspondence',
    factory: makeComplianceLetterPreset,
  },
] as const;
